import { Controller } from 'stimulus';
import { formatCurrency } from '@/js/shared/billables/currency';
import { decimal } from '@/js/shared/billables/decimal';

const HIGH_PERCENTAGE = '50';

export default class extends Controller {
  static targets = [
    'calculatedAmount',
    'methodSelect',
    'amountWrapper',
    'amountInput',
    'amountField',
    'percentageWrapper',
    'percentageInput',
    'percentageField',
    'perItemToggle',
    'error',
    'warning',
    'warningPercentage',
  ];

  static values = {
    currencySymbol: String,
    currencySide: String,
  };

  #warningDismissed = false;

  get calculationInput() {
    if (!this.hasMethodSelectTarget) {
      return { method: 'per_line' };
    }

    return {
      method: this.methodSelectTarget.value,
      amount: this.amountInputTarget.value || '0',
      percentage: this.percentageInputTarget.value || '0',
    };
  }

  connect() {
    this.#syncInputState();
  }

  changeMethod() {
    this.#syncInputState();
    this.#syncValidity();
    this.#hideWarning();
    this.dispatch('method-changed', { detail: { method: this.methodSelectTarget.value } });
    this.dispatch('changed');
  }

  update() {
    this.#syncInputState();
    this.#syncValidity();
    this.#syncWarning();
    this.dispatch('changed');
  }

  dismissWarning() {
    this.#warningDismissed = true;
    this.#hideWarning();
  }

  renderCalculatedAmount({ shipping, currency }) {
    this.calculatedAmountTarget.textContent = formatCurrency(shipping, currency, {
      useGrouping: false,
    });
  }

  get #activeInput() {
    switch (this.methodSelectTarget.value) {
      case 'flat':
        return this.amountInputTarget;
      case 'percentage':
        return this.percentageInputTarget;
      default:
        return null;
    }
  }

  #syncValidity() {
    if (!this.hasMethodSelectTarget) {
      return;
    }

    const input = this.#activeInput;
    const valid = !input || input.validity.valid;

    this.amountInputTarget.classList.toggle(
      'field--error',
      !valid && input === this.amountInputTarget
    );
    this.percentageInputTarget.classList.toggle(
      'field--error',
      !valid && input === this.percentageInputTarget
    );
    this.element.classList.toggle('billable-shipping--invalid', !valid);
    this.errorTarget.textContent = valid ? '' : this.#errorMessage(input.validity, input.max);
    this.dispatch('validity-changed', { detail: { valid } });
  }

  #errorMessage(validity, max) {
    if (validity.rangeUnderflow) {
      return "Shipping can't be negative";
    }
    if (validity.rangeOverflow) {
      return `Shipping can't be more than ${formatCurrency(max, this.#currency)}`;
    }
    return 'Enter a valid number';
  }

  get #currency() {
    return { symbol: this.currencySymbolValue, side: this.currencySideValue };
  }

  #syncWarning() {
    if (!this.hasWarningTarget || this.#warningDismissed) {
      return;
    }

    const { method, percentage } = this.calculationInput;
    const value = decimal(percentage);

    if (method !== 'percentage' || !value.gt(HIGH_PERCENTAGE)) {
      this.#hideWarning();
      return;
    }

    this.warningPercentageTarget.textContent = value.toString();
    this.warningTarget.classList.remove('hide');
  }

  #hideWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.classList.add('hide');
    }
  }

  #syncInputState() {
    if (!this.hasMethodSelectTarget) {
      return;
    }

    const { method, amount, percentage } = this.calculationInput;
    this.amountWrapperTarget.classList.toggle('hide', method !== 'flat');
    this.percentageWrapperTarget.classList.toggle('hide', method !== 'percentage');
    this.perItemToggleTarget.classList.toggle('hide', method !== 'per_line');
    this.amountInputTarget.disabled = method !== 'flat';
    this.percentageInputTarget.disabled = method !== 'percentage';
    this.amountFieldTarget.value = amount;
    this.percentageFieldTarget.value = percentage;
  }
}
